// Angular Files
import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgModule,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Angular Material Files
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Other External Files
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Teller Online Files
import { AuthorizationConsentService } from 'apps/public-portal/src/app/core/services';
import {
    ActiveAuthorizationTextDto,
    PaymentAuthorizationTypeEnumDto,
    PaymentMethodTypeEnumDto
} from 'apps/public-portal/src/app/core/api/PublicPortalApiClients';
import { AuthorizationConsentDto } from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

// Teller Online Library Files
import { TellerOnlineIconsModule } from 'teller-online-libraries/icons';

// Admin-edited text is WYSIWYG HTML that often ends in empty paragraphs (<p><br></p>); rendered,
// each adds a blank line and a paragraph margin above the submit button. Stripped for display
// only: the consent payload keeps the text exactly as stored, since the server hashes it.
const TRAILING_BLANK_CONTENT = /(?:\s|&nbsp;|<br\s*\/?>|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+$/i;

// Process-wide counter so each instance's disclosure text gets a stable, unique
// DOM id that consumers can target with aria-describedby.
let nextConsentTextId = 0;

// Shown when a submit is rejected because the authorization text changed after
// it was presented. In Inline Mode the disclosure re-renders in place (see
// `replaceVersion`); in Modal Mode the dialog is gone by submit time, so callers
// surface this notice and the user re-authorizes against the updated text on
// their next submit.
export const AUTHORIZATION_REAUTH_NOTICE = 'This authorization notice was updated. '
    + 'Please review and authorize again.';

// Shown when a submit is rejected because the authorization text was turned off after it
// was presented. The disclosure disappears, so this is the payer's cue to submit again.
export const AUTHORIZATION_REMOVED_NOTICE = 'This authorization notice is no longer '
    + 'required. Please submit again.';

/**
 * Reusable authorization disclosure component (TOP-2935). Renders the full
 * active authorization text inline; consent is captured as inline clickwrap —
 * the host's primary submit button ("Authorize and submit payment") is the
 * binding consent act, so there is no checkbox here.
 *
 * Parent integration contract:
 * - Bind `[paymentMethodType]` and `[authorizationType]` to the relevant enums
 *   for the current flow.
 * - Listen to `(consentChange)` for an `AuthorizationConsentDto` payload (the
 *   `{ versionId, text }` the user saw) that the parent must include verbatim
 *   in its submit request, OR `null` when no consent is required (legitimate
 *   only for Credit-card flows where no version resolves via the AdHoc
 *   fallback chain). A non-null payload + the user clicking submit IS consent.
 * - On a `StaleAuthorizationVersion` error from a submit, call
 *   `replaceVersion(newActive)` (passing the new active version echoed in the
 *   error response) to re-render the disclosure inline rather than refresh the
 *   whole page.
 * - Accessibility: the disclosure text is a labelled region whose element id is
 *   exposed as `textElementId`. Because the clickwrap binds consent to the
 *   primary submit button, the host should bind that button's
 *   `[attr.aria-describedby]` to this `textElementId` (via the `componentReady`
 *   reference) so screen-reader users hear the authorization terms announced as
 *   the description of the action they are about to take.
 */
@Component({
    selector: 'app-authorization-consent',
    templateUrl: './authorization-consent.component.html',
    styleUrls: ['./authorization-consent.component.scss'],
    host: {
        class: 'authorization-consent'
    },
    standalone: false
})
export class AuthorizationConsentComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public paymentMethodType: PaymentMethodTypeEnumDto;

    @Input() public authorizationType: PaymentAuthorizationTypeEnumDto;

    // When the host has already fetched the active text (e.g. the modal consent
    // dialog's pre-check), pass it here so this component skips its own fetch.
    // A single fetch means the disclosure can't diverge from what the host saw
    // (avoids the dialog's "Authorize" button going permanently dead).
    @Input() public presetActiveText?: ActiveAuthorizationTextDto;

    @Output() public consentChange: EventEmitter<AuthorizationConsentDto | null> = new EventEmitter<AuthorizationConsentDto | null>();

    @Output() public componentReady: EventEmitter<AuthorizationConsentComponent> = new EventEmitter<AuthorizationConsentComponent>();

    public loading: boolean = false;

    public activeVersion: ActiveAuthorizationTextDto | null = null;

    // Sanitization is a backend contract (HtmlSanitizer.Sanitize at
    // AuthorizationTextVersionManager.CreateVersionAsync) — this trusts the
    // already-sanitized text, it does not sanitize.
    public safeHtmlText: SafeHtml | null = null;

    public showRefreshedBanner: boolean = false;

    /**
     * Stable, unique DOM id for the disclosure text element. Hosts bind their
     * primary submit button's `[attr.aria-describedby]` to this so the clickwrap
     * terms are announced as the description of the submit action.
     */
    public readonly textElementId = `authorization-consent-text-${nextConsentTextId++}`;

    private _destroy$ = new Subject<void>();

    constructor(
        private authorizationConsentService: AuthorizationConsentService,
        private sanitizer: DomSanitizer,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.componentReady.emit(this);

        // Seeded by the host with already-fetched text: render it directly
        // instead of issuing a second getActive (single source of truth).
        if (this.presetActiveText !== undefined) {
            this._applyActive(this.presetActiveText);
            return;
        }

        this.fetchActive();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.paymentMethodType && !changes.authorizationType) return;
        // Skip the initial change before ngOnInit fires.
        if (changes.paymentMethodType?.firstChange && changes.authorizationType?.firstChange) return;
        this.fetchActive();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    /**
     * Called by the parent component after catching a
     * `StaleAuthorizationVersion` error response. Re-renders the disclosure
     * onto the new active version and surfaces a refreshed-text banner. The
     * parent's other form state is preserved because only this component
     * re-renders. `null` means the text was turned off: the disclosure is
     * cleared and no consent is emitted.
     */
    public replaceVersion(newActive: ActiveAuthorizationTextDto | null): void {
        this.activeVersion = newActive;
        this.safeHtmlText = this._toSafeHtml(newActive);
        this.showRefreshedBanner = !!newActive;
        this.emitConsent();
        this.cdr.markForCheck();
    }

    /** Re-fetches the active text from the server, e.g. when a rejection doesn't carry it. */
    public refresh(): void {
        this.fetchActive();
    }

    private fetchActive(): void {
        this.loading = true;
        this.showRefreshedBanner = false;
        this.emitConsent();

        this.authorizationConsentService
            .getActive(this.paymentMethodType, this.authorizationType)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: active => this._applyActive(active),
                // Failure to resolve falls back to "no active text" rather than
                // blocking the whole flow. The server-side ECheck-required
                // enforcement will still reject the submit if it's an ECheck
                // flow without an active text, so we don't silently let bad
                // ECheck submits through.
                error: () => this._applyActive(null)
            });
    }

    private _applyActive(active: ActiveAuthorizationTextDto | null): void {
        this.activeVersion = active;
        this.safeHtmlText = this._toSafeHtml(active);
        this.loading = false;
        this.emitConsent();
    }

    private _toSafeHtml(active: ActiveAuthorizationTextDto | null): SafeHtml | null {
        if (!active) return null;

        return this.sanitizer.bypassSecurityTrustHtml(
            active.text.replace(TRAILING_BLANK_CONTENT, ''));
    }

    private emitConsent(): void {
        if (!this.activeVersion) {
            this.consentChange.emit(null);
            return;
        }
        this.consentChange.emit(new AuthorizationConsentDto({
            versionId: this.activeVersion.versionId,
            text: this.activeVersion.text
        }));
    }
}

@NgModule({
    imports: [
        CommonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TellerOnlineIconsModule
    ],
    declarations: [AuthorizationConsentComponent],
    exports: [AuthorizationConsentComponent]
})
export class AuthorizationConsentModule {}
